import React, { useEffect, useState } from "react";
import { Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { deleteEpass, listEpasses, saveEpass } from "../../../services/epassApi";

const blank = {
  name: "",
  applyLink: "",
  instructions: "",
  displayOrder: 0,
  active: true,
};

export default function EpassesManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listEpasses();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load e-passes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (field, value) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = normalize(editing);
      const data = await saveEpass(payload);
      setRows((prev) =>
        payload.id ? prev.map((row) => (row.id === data.id ? data : row)) : [...prev, data],
      );
      setEditing(blank);
      toast.success("E-pass saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save e-pass");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this e-pass?")) return;
    try {
      await deleteEpass(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not delete e-pass");
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">E-Passes</div>
        <div className="text-muted small">
          Permits a vehicle needs to enter a place. Pick them per trip in Dynamic Pricing, with the day they are needed on.
        </div>
      </Card.Header>
      <Card.Body>
        <Card className="mb-3">
          <Card.Body>
            <div className="fw-semibold mb-3">{editing.id ? "Edit E-Pass" : "New E-Pass"}</div>
            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={editing.name || ""}
                  onChange={(e) => patch("name", e.target.value)}
                  placeholder="e.g. Baba Budangiri & Jhari Falls"
                />
              </div>
              <div className="col-md-5">
                <Form.Label>Apply Link</Form.Label>
                <Form.Control
                  value={editing.applyLink || ""}
                  onChange={(e) => patch("applyLink", e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="col-md-1">
                <Form.Label>Order</Form.Label>
                <Form.Control type="number" value={editing.displayOrder || 0} onChange={(e) => patch("displayOrder", Number(e.target.value || 0))} />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Form.Check type="switch" label="Active" checked={editing.active !== false} onChange={(e) => patch("active", e.target.checked)} />
              </div>
              <div className="col-12">
                <Form.Label>How to apply</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editing.instructions || ""}
                  onChange={(e) => patch("instructions", e.target.value)}
                  placeholder="What is needed (vehicle number, driver licence...), how early it opens"
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="outline-secondary" onClick={() => setEditing(blank)}>Clear</Button>
              <Button disabled={saving || !String(editing.name || "").trim()} onClick={save}>{saving ? "Saving..." : "Save E-Pass"}</Button>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="py-5 text-center"><Spinner /></div>
        ) : (
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>Name</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr><td colSpan={4} className="text-muted text-center py-3">No e-passes yet.</td></tr>
              )}
              {rows.map((row, index) => (
                <tr key={row.id}><td className="small fw-semibold">{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{row.name}</div>
                    {row.instructions && <div className="small text-muted" style={{ whiteSpace: "pre-line" }}>{row.instructions}</div>}
                  </td>
                  <td>{row.active ? "Active" : "Inactive"}</td>
                  <td className="text-end text-nowrap">
                    {row.applyLink ? <Button as="a" href={row.applyLink} target="_blank" rel="noreferrer" size="sm" variant="outline-primary" className="me-2">Apply</Button> : null}
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => setEditing(row)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(row.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

function normalize(row) {
  return {
    ...row,
    name: String(row.name || "").trim(),
    applyLink: String(row.applyLink || "").trim(),
    instructions: String(row.instructions || "").trim(),
    displayOrder: Number(row.displayOrder || 0),
    active: row.active !== false,
  };
}
